/**
 * The board answering back through the phone.
 *
 * There is no amplitude control on the web - navigator.vibrate takes durations
 * and the gaps between them, nothing else - so weight here is spelled in
 * rhythm. A pulse that lengthens while its gaps close is the only "rising" this
 * API can say, and it is what the win and the arriving turn are built from.
 *
 * Nothing outside this file holds a duration, for the same reason SURGES holds
 * every flicker timing: a cue whose numbers are scattered cannot be tuned. What
 * the moments themselves are, and how they rank against each other, is in
 * cues.js - this file only says what each one feels like.
 *
 * iOS has no Vibration API at all - not partial, absent - and every browser on
 * it is the same engine underneath, so adding the game to a home screen does
 * not change it either. This is an Android enhancement that lands as silence
 * everywhere else, which is why nothing anywhere waits on it or reads its
 * answer.
 */

import { ACTIONS, weightOf } from './cues.js';
import { reducedMotion } from './surge.js';

/**
 * Every cue in the app as a vibrate pattern: pulse, gap, pulse, and so on.
 *
 * All odd-length, so each ends on a pulse. A trailing gap is time the player
 * spends waiting for something that has already finished happening.
 */
export const TAPS = {
    peg: [10],
    /** Two taps at once is one tap, so the fifth peg lands as a heavier one. */
    full: [18],
    /** Two beats, because undoing is not doing. */
    clear: [8, 40, 8],
    hold: [22],
    copied: [12],

    /** The turn coming to you: two beats, the second the heavier. */
    turn: [12, 70, 22],
    poke: [10, 60, 10],

    /** One flat beat that goes nowhere, like the guess. */
    dead: [34],
    near: [10, 50, 10],
    close: [12, 30, 24],
    /** Flat and equal: the shape of "no". */
    refused: [28, 60, 28],

    /** The code going. The pulses grow as their gaps close, then it holds. */
    win: [10, 40, 12, 34, 16, 28, 20, 22, 90],
    /** The same shape, running down. */
    lost: [40, 90, 60],
    /** One weight, with nothing answering it. */
    forfeit: [60],
};

/** Which cue a scored row of your own earns. Mirrors what explainResult says. */
export function scoreTap(result) {
    const placement = Number(result && result.placement) || 0;
    const total = Number(result && result.total) || 0;

    if (placement > 0) return 'close';
    if (total > 0) return 'near';
    return 'dead';
}

/** How long a tap occupies the motor, its pulses and gaps together. */
export function durationOf(name) {
    const pattern = TAPS[name];
    if (!pattern) throw new Error(`tap: no preset named ${name}`);
    return pattern.reduce((total, ms) => total + ms, 0);
}

/**
 * The hand channel.
 *
 * vibrate() cancels whatever is already running, so a reaction landing during
 * the win would saw the celebration in half. Hence the one rule this holds
 * state for: a tap interrupts only bigger news than whatever has the motor.
 *
 * Two different questions, kept apart. How long the motor stays busy is a
 * duration, because that is literally how long it is moving. Whether an
 * arriving tap may take it is weight, because that is a judgement about the
 * game and not about the hardware. Duration used to answer both, on the
 * observation that the cues that matter are the long ones - true, but only by
 * arrangement, and it made every retiming a silent change to what interrupts
 * what.
 *
 * Reduced motion silences it. Haptics is not motion and does not trigger the
 * vestibular trouble that setting exists for, but it is the only signal a
 * browser offers about wanting the interface to stop doing things, and this is
 * a thing the interface does. It stays this channel's own gate: the mute on the
 * home page is about noise, and a player who asked for less motion did not ask
 * about noise.
 */
export function createHaptics({ scope = globalThis, now = () => Date.now() } = {}) {
    let busyUntil = 0;
    let busyWeight = 0;

    return {
        /**
         * Always on. There is no preference behind this - the motor is either
         * there or it is not, and a phone answers that question by existing.
         */
        enabled: () => true,

        play(name) {
            const weight = weightOf(name);

            const nav = scope && scope.navigator;
            if (!nav || typeof nav.vibrate !== 'function') return false;
            if (reducedMotion(scope)) return false;

            const at = now();
            if (at < busyUntil && weight <= busyWeight) return false;

            // A browser that refuses - no user activation yet, or a hidden page
            // - says so by returning false, and a tap that never ran has no
            // claim on the motor. Older engines answer with undefined and are
            // taken at their word.
            const fired = nav.vibrate(TAPS[name]) !== false;
            if (!fired) return false;

            busyUntil = at + durationOf(name);
            busyWeight = weight;
            return true;
        },
    };
}

/** Every moment has a pattern, or somewhere in the app taps into nothing. */
export function missingTaps() {
    return Object.keys(ACTIONS).filter((name) => !TAPS[name]);
}
